import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  CATALOG_ATTACHMENT_MAX_COUNT,
  CATALOG_FILE_ACCEPT,
  CATALOG_IMAGE_ACCEPT,
  CatalogAttachment,
  CatalogFileTypeTone,
  formatCatalogFileSize,
  resolveCatalogFileType,
  splitCatalogAttachments,
} from './catalogAttachments';
import styles from './Catalog.module.css';

type CatalogMediaPanelProps = {
  ns: 'products' | 'services' | 'assets';
  itemName: string;
  attachments: CatalogAttachment[];
  canUpdate: boolean;
  busy?: boolean;
  onUpload: (file: File, setAsPrimary: boolean) => Promise<void>;
  onRemove: (attachmentId: string) => Promise<void>;
  onSetPrimary: (attachmentId: string) => Promise<void>;
};

const FILE_TONE_ROW: Record<CatalogFileTypeTone, string> = {
  pdf: styles.attachmentRowPdf,
  doc: styles.attachmentRowDoc,
  sheet: styles.attachmentRowSheet,
  slide: styles.attachmentRowSlide,
  archive: styles.attachmentRowArchive,
  text: styles.attachmentRowText,
  file: styles.attachmentRowFile,
};

const FILE_TONE_BADGE: Record<CatalogFileTypeTone, string> = {
  pdf: styles.attachmentBadgePdf,
  doc: styles.attachmentBadgeDoc,
  sheet: styles.attachmentBadgeSheet,
  slide: styles.attachmentBadgeSlide,
  archive: styles.attachmentBadgeArchive,
  text: styles.attachmentBadgeText,
  file: styles.attachmentBadgeFile,
};

export const CatalogMediaPanel: React.FC<CatalogMediaPanelProps> = ({
  ns,
  itemName,
  attachments,
  canUpdate,
  busy = false,
  onUpload,
  onRemove,
  onSetPrimary,
}) => {
  const { t } = useTranslation([ns, 'common']);
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [working, setWorking] = React.useState(false);
  const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);
  const disabled = busy || working;
  const remainingSlots = Math.max(0, CATALOG_ATTACHMENT_MAX_COUNT - attachments.length);
  const { images, files } = splitCatalogAttachments(attachments);
  const previewImage = previewIndex === null ? null : images[previewIndex] ?? null;

  React.useEffect(() => {
    if (previewIndex === null) return;
    if (images.length === 0) {
      setPreviewIndex(null);
      return;
    }
    if (previewIndex >= images.length) {
      setPreviewIndex(images.length - 1);
    }
  }, [images.length, previewIndex]);

  React.useEffect(() => {
    if (previewIndex === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPreviewIndex(null);
        return;
      }
      if (images.length < 2) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPreviewIndex(current => (current === null ? current : (current + 1) % images.length));
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPreviewIndex(current => (current === null ? current : (current - 1 + images.length) % images.length));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [previewIndex, images.length]);

  const uploadFiles = async (selected: File[], setAsPrimary: boolean) => {
    if (!canUpdate || selected.length === 0) return;
    const limited = selected.slice(0, remainingSlots);
    if (limited.length === 0) return;
    setWorking(true);
    try {
      for (let index = 0; index < limited.length; index += 1) {
        await onUpload(limited[index], setAsPrimary && index === 0);
      }
    } finally {
      setWorking(false);
    }
  };

  const onImagesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    await uploadFiles(selected, false);
  };

  const onFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    await uploadFiles(selected, false);
  };

  const confirmRemove = async (attachment: CatalogAttachment) => {
    if (!window.confirm(t('removeAttachmentConfirm', { ns, fileName: attachment.fileName }))) {
      return;
    }
    setWorking(true);
    try {
      await onRemove(attachment.id);
    } finally {
      setWorking(false);
    }
  };

  const setPrimary = async (attachmentId: string) => {
    setWorking(true);
    try {
      await onSetPrimary(attachmentId);
    } finally {
      setWorking(false);
    }
  };

  const showPrevious = () => {
    setPreviewIndex(current => (current === null || images.length === 0
      ? current
      : (current - 1 + images.length) % images.length));
  };

  const showNext = () => {
    setPreviewIndex(current => (current === null || images.length === 0
      ? current
      : (current + 1) % images.length));
  };

  return (
    <div className={styles.mediaPanel}>
      <input
        ref={imageInputRef}
        className={styles.hiddenInput}
        type="file"
        accept={CATALOG_IMAGE_ACCEPT}
        multiple
        onChange={event => void onImagesSelected(event)}
      />
      <input
        ref={fileInputRef}
        className={styles.hiddenInput}
        type="file"
        accept={CATALOG_FILE_ACCEPT}
        multiple
        onChange={event => void onFilesSelected(event)}
      />

      <section className={styles.mediaSection}>
        <div className={styles.mediaSectionHeader}>
          <div>
            <p className={styles.detailLabel}>{t('photos', { ns })}</p>
            <p className={styles.fieldHint}>{t('photosHint', { ns })}</p>
          </div>
          {canUpdate ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => imageInputRef.current?.click()}
              disabled={disabled || remainingSlots === 0}
            >
              {t('addPhotos', { ns })}
            </button>
          ) : null}
        </div>
        {images.length === 0 ? (
          <p className={styles.fieldHint}>{t('noPhotos', { ns })}</p>
        ) : (
          <div className={styles.mediaGallery}>
            {images.map((image, index) => (
              <article key={image.id} className={`${styles.mediaCard} ${image.isPrimary ? styles.mediaCardPrimary : ''}`}>
                <button
                  type="button"
                  className={styles.mediaPreviewButton}
                  onClick={() => setPreviewIndex(index)}
                  aria-label={t('viewFullSize', { ns: 'common' })}
                >
                  <img className={styles.mediaPreview} src={image.url} alt={image.fileName || itemName} />
                  <span className={styles.mediaPreviewExpand} aria-hidden="true">⤢</span>
                  <span className={styles.mediaPreviewHint}>{t('viewFullSize', { ns: 'common' })}</span>
                </button>
                {image.isPrimary ? <span className={styles.defaultBadge}>{t('defaultPhoto', { ns })}</span> : null}
                <div className={styles.mediaCardBody}>
                  <p className={styles.mediaFileName} title={image.fileName}>{image.fileName}</p>
                  {canUpdate ? (
                    <div className={styles.photoActions}>
                      {image.isPrimary ? null : (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => void setPrimary(image.id)}
                          disabled={disabled}
                        >
                          {t('setAsDefault', { ns })}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => void confirmRemove(image)}
                        disabled={disabled}
                      >
                        {t('removePhoto', { ns })}
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={styles.mediaSection}>
        <div className={styles.mediaSectionHeader}>
          <div>
            <p className={styles.detailLabel}>{t('attachments', { ns })}</p>
            <p className={styles.fieldHint}>{t('attachmentsHint', { ns })}</p>
          </div>
          {canUpdate ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || remainingSlots === 0}
            >
              {t('addAttachment', { ns })}
            </button>
          ) : null}
        </div>
        {files.length === 0 ? (
          <p className={styles.fieldHint}>{t('noAttachments', { ns })}</p>
        ) : (
          <ul className={styles.attachmentList}>
            {files.map(file => {
              const type = resolveCatalogFileType(file.fileName, file.contentType);
              return (
                <li key={file.id} className={`${styles.attachmentRow} ${FILE_TONE_ROW[type.tone]}`}>
                  <a
                    className={styles.attachmentMain}
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${t('openFile', { ns: 'common' })}: ${file.fileName}`}
                  >
                    <span className={`${styles.attachmentBadge} ${FILE_TONE_BADGE[type.tone]}`} aria-hidden="true">
                      {type.badge}
                    </span>
                    <span className={styles.attachmentMeta}>
                      <span className={styles.attachmentName}>{file.fileName}</span>
                      <span className={styles.attachmentTypeLine}>
                        {t(type.labelKey, { ns: 'common' })}
                        <span className={styles.attachmentTypeDot} aria-hidden="true">·</span>
                        {formatCatalogFileSize(file.fileSize)}
                      </span>
                    </span>
                  </a>
                  {canUpdate ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void confirmRemove(file)}
                      disabled={disabled}
                    >
                      {t('removeAttachment', { ns })}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {previewImage && typeof document !== 'undefined'
        ? createPortal(
          <div
            className={styles.lightboxOverlay}
            role="dialog"
            aria-modal="true"
            aria-label={t('photoPreview', { ns: 'common' })}
            onClick={() => setPreviewIndex(null)}
          >
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={() => setPreviewIndex(null)}
              aria-label={t('close', { ns: 'common' })}
            >
              {t('close', { ns: 'common' })}
            </button>
            {images.length > 1 ? (
              <button
                type="button"
                className={`${styles.lightboxNav} ${styles.lightboxNavPrev}`}
                onClick={event => {
                  event.stopPropagation();
                  showPrevious();
                }}
                aria-label={t('previous', { ns: 'common' })}
              >
                ‹
              </button>
            ) : null}
            {images.length > 1 ? (
              <button
                type="button"
                className={`${styles.lightboxNav} ${styles.lightboxNavNext}`}
                onClick={event => {
                  event.stopPropagation();
                  showNext();
                }}
                aria-label={t('next', { ns: 'common' })}
              >
                ›
              </button>
            ) : null}
            <div className={styles.lightboxContent} onClick={event => event.stopPropagation()}>
              <img
                className={styles.lightboxImage}
                src={previewImage.url}
                alt={previewImage.fileName || itemName}
              />
              <p className={styles.lightboxCaption}>
                <span>{previewImage.fileName || itemName}</span>
                {images.length > 1 ? (
                  <span>{t('photoOf', { ns: 'common', current: (previewIndex ?? 0) + 1, total: images.length })}</span>
                ) : null}
              </p>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
};
